"use client"

import * as React from "react"
import Link from "next/link"
import { Header } from "@/components/header"
import { Footer } from "@/components/footer"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ShoppingBag, Sparkles } from "lucide-react"
import { products, categories } from "@/lib/merch-data"
import { CartProvider, useCart } from "@/lib/cart-context"

function MerchContent() {
  const [activeCategory, setActiveCategory] = React.useState("All")
  const { itemCount } = useCart()

  const filteredProducts = activeCategory === "All"
    ? products
    : products.filter(p => p.category === activeCategory)

  const featuredProducts = products.filter(p => p.featured)

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Hero */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10" />
          <div className="relative mx-auto max-w-6xl px-6 lg:px-8 py-12">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-2">Merch</h1>
                <p className="text-lg text-muted-foreground">
                  Rep your league. Look good doing it.
                </p>
              </div>
              <Button variant="outline" className="w-fit" asChild>
                <Link href="/merch/cart">
                  <ShoppingBag className="mr-2 h-4 w-4" />
                  Cart ({itemCount})
                </Link>
              </Button>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          {/* Featured Banner */}
          {featuredProducts.length > 0 && activeCategory === "All" && (
            <div className="mb-8 p-4 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-lg border border-primary/20">
              <div className="flex items-center gap-2 text-sm text-primary">
                <Sparkles className="h-4 w-4" />
                <span className="font-medium">Featured: {featuredProducts.map(p => p.name).join(", ")}</span>
              </div>
            </div>
          )}

          {/* Category Filter */}
          <div className="flex gap-2 mb-8 flex-wrap">
            {categories.map(category => (
              <Button
                key={category}
                variant={activeCategory === category ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveCategory(category)}
                className={activeCategory === category ? "bg-primary" : ""}
              >
                {category}
              </Button>
            ))}
          </div>

          {/* Product Grid */}
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredProducts.map(product => (
              <Link key={product.id} href={`/merch/${product.slug}`}>
                <Card className="bg-card border-border hover:border-primary/50 transition-all hover:-translate-y-1 h-full">
                  <CardContent className="p-0">
                    {/* Product Image */}
                    <div className="aspect-square bg-gradient-to-br from-secondary to-muted flex items-center justify-center relative">
                      <span className="text-4xl opacity-30">GNL</span>
                      {product.featured && (
                        <Badge className="absolute top-2 right-2 bg-primary/90">Featured</Badge>
                      )}
                      {!product.inStock && (
                        <Badge variant="secondary" className="absolute top-2 left-2">Sold Out</Badge>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="p-4">
                      <p className="text-xs text-muted-foreground mb-1">{product.category}</p>
                      <h3 className="font-semibold text-foreground mb-2">{product.name}</h3>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-foreground">${product.price}</span>
                        {product.colors && (
                          <div className="flex gap-1">
                            {product.colors.slice(0, 3).map(color => (
                              <div
                                key={color.name}
                                className="w-4 h-4 rounded-full border border-border"
                                style={{ backgroundColor: color.hex }}
                                title={color.name}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-12">
              <ShoppingBag className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No products found in this category.</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default function MerchPage() {
  return (
    <CartProvider>
      <MerchContent />
    </CartProvider>
  )
}
